import React, { useEffect, useRef, useState } from "react";
import {
  DollarSign, Lock, Upload, FileSpreadsheet, X, Edit3, Trash2, Plus,
  AlertTriangle, CheckCircle, Eye, EyeOff, Settings, ChevronDown, ChevronRight,
  Link2, BarChart3,
} from "lucide-react";
import { portalApi } from "../../lib/portalApi";
import { normalizeDateInput } from "./FinanceLedgerSection";
import VendorPackageModal from "./VendorPackageModal";

const PAYMENT_STATUSES = [
  { v: "not_due", l: "Not Due", cls: "bg-[#7A6B5F] text-white" },
  { v: "due", l: "Due", cls: "bg-[#D4A963] text-[#1C110C]" },
  { v: "invoice_issued", l: "Invoice Issued", cls: "bg-[#6E1F2C] text-white" },
  { v: "partially_paid", l: "Partially Paid", cls: "bg-[#3E2A22] text-white" },
  { v: "paid", l: "Paid", cls: "bg-[#3F6E2C] text-white" },
  { v: "overdue", l: "Overdue", cls: "bg-[#A3141A] text-white" },
  { v: "on_hold", l: "On Hold", cls: "bg-[#1C110C] text-white" },
];

const SHARE_TOGGLE_FIELDS = [
  { k: "share_summary_with_client", l: "Share project value & summary with client" },
  { k: "share_milestones_with_client", l: "Share payment milestone schedule with client" },
  { k: "share_stages_with_client", l: "Share stage / BOQ value breakdown with client" },
  { k: "share_external_supply_with_client", l: "Share external supply responsibility with client" },
  { k: "share_notes_with_client", l: "Share client-facing financial notes" },
];

const VIEW_TIER_OPTIONS = [
  { k: "admin", l: "Main Admin (always)" },
  { k: "project_manager", l: "Project Manager" },
];
const VIEW_ROLE_OPTIONS = [
  { k: "admin_staff", l: "Admin Staff" },
];

function fmtAED(n) {
  if (n === null || n === undefined || n === "") return "—";
  const v = Number(n);
  if (!isFinite(v)) return "—";
  return `AED ${v.toLocaleString("en-AE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtPct(n) {
  if (n === null || n === undefined || n === "") return "—";
  const v = Number(n);
  // values may come as either fraction (0.17) or whole percent (17)
  const pct = v > 1.5 ? v : v * 100;
  return `${pct.toFixed(1)}%`;
}
function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
}

/* ---------- Summary cards ---------- */
function StatCard({ label, value, hint, accent = "maroon", lock, testid }) {
  const styles = {
    maroon: "border-[#6E1F2C]/15",
    gold: "border-[#D4A963]/40 bg-[#FAF5EC]",
    red: "border-[#A3141A]/30",
    green: "border-[#3F6E2C]/30",
  };
  return (
    <div data-testid={testid} className={`bg-white border ${styles[accent]} p-4 relative`}>
      {lock && (
        <div className="absolute top-2 right-2 inline-flex items-center gap-1 text-[#A6822F]">
          <Lock size={10} />
          <span className="text-[8px] tracking-[0.22em] uppercase">Admin</span>
        </div>
      )}
      <p className="text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] mb-1">{label}</p>
      <p className="font-serif-display text-xl text-[#1C110C] leading-tight break-words">{value}</p>
      {hint && <p className="text-[10px] text-[#7A6B5F] mt-1.5 italic">{hint}</p>}
    </div>
  );
}

function ProgressBar({ pct }) {
  const v = Math.max(0, Math.min(100, pct || 0));
  return (
    <div className="h-2 bg-[#FAF5EC] rounded-full overflow-hidden">
      <div className="h-full bg-gradient-to-r from-[#D4A963] via-[#A6822F] to-[#6E1F2C]" style={{ width: `${v}%` }} />
    </div>
  );
}

/* ---------- Upload + preview-before-import wizard ---------- */
function UploadWizard({ slug, onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState("");
  const [replace, setReplace] = useState(true);

  const runPreview = async () => {
    if (!file) return;
    setBusy(true); setErr("");
    try {
      const d = await portalApi.previewFinancialsUpload(slug, file);
      setPreview(d);
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const runImport = async () => {
    if (!preview) return;
    setBusy(true); setErr("");
    try {
      const sheetRows = (entity) => {
        const s = preview.sheets.find((x) => x.entity === entity);
        return s ? (s.rows || []) : null;
      };
      const body = {
        payment_milestones: sheetRows("payment_milestones"),
        stage_details: sheetRows("stage_details"),
        external_supply: sheetRows("external_supply"),
        replace_existing: replace,
        apply_totals: true,
        payment_total: preview.summary?.payment_total,
        specialized_supply_total: preview.summary?.specialized_supply_total,
        stage_total: preview.summary?.stage_total,
      };
      await portalApi.importFinancials(slug, body);
      onImported && onImported();
      onClose();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-[#1C110C]/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white w-full max-w-[820px] border border-[#6E1F2C]/20 my-6" onClick={(e) => e.stopPropagation()} data-testid="portal-financials-upload-wizard">
        <div className="px-6 py-4 border-b border-[#6E1F2C]/10 flex items-center justify-between bg-gradient-to-r from-white to-[#FAF5EC]">
          <div>
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#A6822F] inline-flex items-center gap-1.5"><Lock size={10} /> Admin only</p>
            <h3 className="font-serif-display text-2xl text-[#1C110C] mt-1">Import payment milestone Excel</h3>
            <p className="text-xs text-[#7A6B5F] mt-1">.xlsx / .xlsm · max 100 MB · preview before commit</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="text-[#7A6B5F] hover:text-[#1C110C]"><X size={20} /></button>
        </div>

        <div className="p-6 space-y-5">
          {!preview && (
            <>
              <label htmlFor="portal-financials-file" className="border-2 border-dashed border-[#6E1F2C]/30 hover:border-[#6E1F2C] block p-8 text-center cursor-pointer transition-colors">
                <FileSpreadsheet className="mx-auto text-[#6E1F2C] mb-2" size={32} />
                {file ? (
                  <>
                    <p className="text-sm text-[#1C110C]">{file.name}</p>
                    <p className="text-[10px] text-[#7A6B5F] mt-1">{(file.size / 1024).toFixed(0)} KB</p>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-[#1C110C]">Click to select your payment milestone Excel</p>
                    <p className="text-[10px] text-[#7A6B5F] mt-1">Expected sheets: Payment Proposal · Stage Details · External Supply Milestones</p>
                  </>
                )}
                <input
                  id="portal-financials-file"
                  data-testid="portal-financials-upload-file"
                  type="file"
                  accept=".xlsx,.xlsm,.xls"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="hidden"
                />
              </label>
              <button
                type="button"
                disabled={!file || busy}
                onClick={runPreview}
                data-testid="portal-financials-preview-btn"
                className="w-full bg-[#6E1F2C] text-[#FAF5EC] px-5 py-3 text-[11px] tracking-[0.28em] uppercase hover:bg-[#56151F] disabled:opacity-50 inline-flex items-center justify-center gap-2"
              >
                <Upload size={14} />
                {busy ? "Reading workbook…" : "Preview workbook"}
              </button>
            </>
          )}

          {preview && (
            <div data-testid="portal-financials-preview">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                <StatCard label="Payment milestones" value={preview.summary.payment_milestones_count} accent="maroon" />
                <StatCard label="Stage line items" value={preview.summary.stage_details_count} accent="gold" />
                <StatCard label="External supply" value={preview.summary.external_supply_count} accent="green" />
                <StatCard label="Total main works" value={fmtAED(preview.summary.payment_total)} accent="maroon" />
              </div>
              {preview.summary.specialized_supply_total != null && (
                <p className="text-[11px] text-[#7A6B5F] mb-4">
                  Detected specialized supply / client-paid items: <span className="text-[#1C110C] font-medium">{fmtAED(preview.summary.specialized_supply_total)}</span>
                </p>
              )}
              {preview.summary.warnings?.length > 0 && (
                <div className="bg-[#D4A963]/15 border border-[#D4A963]/40 p-3 mb-4 text-[11px] text-[#3E2A22]">
                  {preview.summary.warnings.map((w, i) => (
                    <p key={i} className="flex items-start gap-2"><AlertTriangle size={12} className="text-[#A6822F] mt-0.5 shrink-0" />{w}</p>
                  ))}
                </div>
              )}
              <div className="space-y-3">
                {preview.sheets.map((s, i) => (
                  <details key={i} className="border border-[#6E1F2C]/15 bg-[#FAF5EC]/40 group" open={s.entity !== "unknown"}>
                    <summary className="cursor-pointer px-4 py-2.5 flex items-center justify-between">
                      <div>
                        <span className="text-[11px] tracking-[0.24em] uppercase text-[#6E1F2C]">{s.entity === "unknown" ? "Unknown sheet" : s.entity.replace(/_/g, " ")}</span>
                        <span className="ml-2 text-sm text-[#1C110C]">{s.name}</span>
                        <span className="ml-2 text-[11px] text-[#7A6B5F]">· {s.row_count || 0} rows</span>
                      </div>
                      <ChevronDown size={14} className="text-[#7A6B5F]" />
                    </summary>
                    <div className="px-4 py-3 overflow-x-auto text-[11px]">
                      {(s.sample_rows || []).length === 0 ? (
                        <p className="text-[#7A6B5F] italic">No rows extracted from this sheet.</p>
                      ) : (
                        <table className="w-full">
                          <thead>
                            <tr className="text-left text-[10px] tracking-[0.18em] uppercase text-[#7A6B5F]">
                              {Object.keys(s.sample_rows[0]).slice(0, 6).map((k) => (
                                <th key={k} className="pr-3 py-1.5 border-b border-[#6E1F2C]/10">{k.replace(/_/g, " ")}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {s.sample_rows.map((r, ri) => (
                              <tr key={ri} className="border-b border-[#6E1F2C]/5">
                                {Object.keys(s.sample_rows[0]).slice(0, 6).map((k) => (
                                  <td key={k} className="pr-3 py-1.5 text-[#1C110C] align-top">
                                    <span className="line-clamp-2 max-w-[220px] inline-block">
                                      {r[k] === null || r[k] === undefined ? "—" : String(r[k])}
                                    </span>
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </details>
                ))}
              </div>

              <div className="mt-5 border border-[#A6822F]/40 bg-[#FAF5EC] p-4 text-[11px] text-[#3E2A22] space-y-1" data-testid="portal-financials-confirm-summary">
                <p className="text-[10px] tracking-[0.24em] uppercase text-[#A6822F]">Confirm before saving</p>
                {replace && preview.existing && (
                  <p>
                    Existing financial rows to be replaced: <strong>{preview.existing.payment_milestones}</strong> payment milestones,{" "}
                    <strong>{preview.existing.stage_details}</strong> stage breakdown rows,{" "}
                    <strong>{preview.existing.external_supply}</strong> external supply rows.
                  </p>
                )}
                <p>
                  New rows to import: <strong>{preview.summary.payment_milestones_count}</strong> payment milestones,{" "}
                  <strong>{preview.summary.stage_details_count}</strong> stage rows,{" "}
                  <strong>{preview.summary.external_supply_count}</strong> external supply rows.
                  {" "}Duplicates (same milestone code / stage item / package+vendor) are skipped automatically.
                </p>
                <p className="text-[#6E1F2C] font-medium">
                  {preview.safeguard || "Daily updates, photos, reports, comments, files and audit history will not be modified."}
                </p>
              </div>

              <label className="flex items-center gap-2 mt-5 text-[11px] text-[#3E2A22]">
                <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} className="accent-[#6E1F2C]" data-testid="portal-financials-replace-toggle" />
                Replace existing financial data on this project (irreversible)
              </label>

              <div className="flex items-center gap-2 mt-5">
                <button
                  type="button"
                  onClick={() => { setPreview(null); setFile(null); }}
                  className="text-[11px] tracking-[0.22em] uppercase text-[#7A6B5F] hover:text-[#1C110C] px-4 py-2.5 border border-[#6E1F2C]/15"
                >
                  Re-upload
                </button>
                <button
                  type="button"
                  onClick={runImport}
                  disabled={busy}
                  data-testid="portal-financials-import-btn"
                  className="flex-1 bg-[#6E1F2C] text-[#FAF5EC] px-5 py-2.5 text-[11px] tracking-[0.28em] uppercase hover:bg-[#56151F] disabled:opacity-50"
                >
                  {busy ? "Importing…" : "Confirm import"}
                </button>
              </div>
            </div>
          )}

          {err && <p data-testid="portal-financials-upload-error" className="text-[11px] text-[#A3141A]">{err}</p>}
        </div>
      </div>
    </div>
  );
}

/* ---------- Edit project financial fields modal ---------- */
function SettingsModal({ slug, summary, visibility, onClose, onSaved }) {
  const [form, setForm] = useState({
    total_project_value: summary?.total_project_value ?? "",
    contract_value: summary?.contract_value ?? "",
    main_works_value: summary?.main_works_value ?? "",
    specialized_supply_value: summary?.specialized_supply_value ?? "",
    approved_variations: summary?.approved_variations ?? "",
    amount_invoiced: summary?.amount_invoiced ?? "",
    amount_received: summary?.amount_received ?? "",
    balance_pending: summary?.balance_pending ?? "",
    retention_amount: summary?.retention_amount ?? "",
    retention_percentage: summary?.retention_percentage ?? "",
    vat_amount: summary?.vat_amount ?? "",
    vat_percentage: summary?.vat_percentage ?? "",
    financial_notes: summary?.financial_notes ?? "",
    client_notes: summary?.client_notes ?? "",
    payment_status: summary?.payment_status ?? "",
  });
  const [vis, setVis] = useState({ ...visibility });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const payload = {};
      Object.entries(form).forEach(([k, v]) => {
        if (v === "" || v === null || v === undefined) return;
        payload[k] = typeof v === "string" && !k.endsWith("_notes") && k !== "payment_status" ? Number(v) : v;
      });
      payload.visibility = {
        view_tiers: vis.view_tiers,
        view_role_names: vis.view_role_names,
        share_summary_with_client: !!vis.share_summary_with_client,
        share_milestones_with_client: !!vis.share_milestones_with_client,
        share_stages_with_client: !!vis.share_stages_with_client,
        share_external_supply_with_client: !!vis.share_external_supply_with_client,
        share_notes_with_client: !!vis.share_notes_with_client,
      };
      await portalApi.patchFinancialsSettings(slug, payload);
      onSaved && onSaved();
      onClose();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const toggleTier = (t) => {
    setVis((s) => {
      const cur = new Set(s.view_tiers || ["admin"]);
      if (cur.has(t)) cur.delete(t); else cur.add(t);
      cur.add("admin"); // admin always
      return { ...s, view_tiers: Array.from(cur) };
    });
  };
  const toggleRoleName = (r) => {
    setVis((s) => {
      const cur = new Set(s.view_role_names || []);
      if (cur.has(r)) cur.delete(r); else cur.add(r);
      return { ...s, view_role_names: Array.from(cur) };
    });
  };

  const cls = "w-full bg-[#FAF5EC] border border-[#6E1F2C]/20 px-3 py-2 text-[#1C110C] text-sm focus:outline-none focus:border-[#6E1F2C]";
  const lbl = "block text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] mb-1";

  return (
    <div className="fixed inset-0 z-[60] bg-[#1C110C]/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <form onSubmit={submit} data-testid="portal-financials-settings-modal" className="bg-white w-full max-w-[820px] border border-[#6E1F2C]/20 my-6" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-[#6E1F2C]/10 flex items-center justify-between bg-gradient-to-r from-white to-[#FAF5EC]">
          <h3 className="font-serif-display text-2xl text-[#1C110C]">Financial settings & visibility</h3>
          <button type="button" onClick={onClose} className="text-[#7A6B5F] hover:text-[#1C110C]"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-6">
          <section>
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C] mb-3">Project values (AED)</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {[
                ["total_project_value", "Total project value"],
                ["contract_value", "Contract value"],
                ["main_works_value", "Main works value"],
                ["specialized_supply_value", "Specialized supply"],
                ["approved_variations", "Approved variations"],
                ["amount_invoiced", "Amount invoiced"],
                ["amount_received", "Amount received"],
                ["balance_pending", "Balance pending"],
                ["retention_amount", "Retention (AED)"],
                ["retention_percentage", "Retention %"],
                ["vat_amount", "VAT amount"],
                ["vat_percentage", "VAT %"],
              ].map(([k, l]) => (
                <div key={k}>
                  <label className={lbl}>{l}</label>
                  <input
                    type="number" step="0.01"
                    value={form[k]}
                    data-testid={`portal-financials-field-${k}`}
                    onChange={(e) => setForm((s) => ({ ...s, [k]: e.target.value }))}
                    className={cls}
                  />
                </div>
              ))}
            </div>
          </section>
          <section>
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C] mb-3">Notes</p>
            <div className="grid grid-cols-1 gap-3">
              <div>
                <label className={lbl}>Internal financial notes (admin/PM only)</label>
                <textarea rows={2} value={form.financial_notes} onChange={(e) => setForm((s) => ({ ...s, financial_notes: e.target.value }))} className={cls} data-testid="portal-financials-internal-notes" />
              </div>
              <div>
                <label className={lbl}>Client-facing financial notes (shown only when explicitly shared)</label>
                <textarea rows={2} value={form.client_notes} onChange={(e) => setForm((s) => ({ ...s, client_notes: e.target.value }))} className={cls} data-testid="portal-financials-client-notes" />
              </div>
            </div>
          </section>
          <section>
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C] mb-3">Internal access</p>
            <div className="flex flex-wrap gap-2">
              {VIEW_TIER_OPTIONS.map((o) => {
                const active = (vis.view_tiers || []).includes(o.k);
                const disabled = o.k === "admin";
                return (
                  <button
                    type="button" key={o.k} disabled={disabled}
                    data-testid={`portal-financials-tier-${o.k}`}
                    onClick={() => toggleTier(o.k)}
                    className={`text-[11px] tracking-[0.2em] uppercase px-3 py-1.5 border ${active ? "bg-[#6E1F2C] text-white border-[#6E1F2C]" : "bg-white text-[#6E1F2C] border-[#6E1F2C]/30"} ${disabled ? "opacity-70" : ""}`}
                  >
                    {o.l}
                  </button>
                );
              })}
              {VIEW_ROLE_OPTIONS.map((o) => {
                const active = (vis.view_role_names || []).includes(o.k);
                return (
                  <button
                    type="button" key={o.k}
                    data-testid={`portal-financials-rolename-${o.k}`}
                    onClick={() => toggleRoleName(o.k)}
                    className={`text-[11px] tracking-[0.2em] uppercase px-3 py-1.5 border ${active ? "bg-[#6E1F2C] text-white border-[#6E1F2C]" : "bg-white text-[#6E1F2C] border-[#6E1F2C]/30"}`}
                  >
                    {o.l}
                  </button>
                );
              })}
            </div>
            <p className="text-[10px] text-[#7A6B5F] mt-2 italic">Site engineers, subcontractors, suppliers, delivery and housekeeping never see financials regardless of these toggles.</p>
          </section>
          <section>
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#A3141A] mb-3 inline-flex items-center gap-1.5"><EyeOff size={11} /> Client sharing — off by default</p>
            <div className="space-y-2">
              {SHARE_TOGGLE_FIELDS.map((f) => (
                <label key={f.k} className="flex items-center gap-2 cursor-pointer text-[12px] text-[#1C110C]">
                  <input
                    type="checkbox"
                    checked={!!vis[f.k]}
                    data-testid={`portal-financials-${f.k}`}
                    onChange={(e) => setVis((s) => ({ ...s, [f.k]: e.target.checked }))}
                    className="accent-[#6E1F2C]"
                  />
                  {f.l}
                </label>
              ))}
            </div>
          </section>
          {err && <p className="text-[11px] text-[#A3141A]">{err}</p>}
        </div>
        <div className="px-6 py-4 border-t border-[#6E1F2C]/10 flex items-center justify-end gap-2 bg-[#FAF5EC]/40">
          <button type="button" onClick={onClose} className="text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] px-4 py-2">Cancel</button>
          <button type="submit" disabled={busy} data-testid="portal-financials-settings-save" className="bg-[#6E1F2C] text-[#FAF5EC] hover:bg-[#56151F] px-5 py-2.5 text-[10px] tracking-[0.28em] uppercase disabled:opacity-60">
            {busy ? "Saving…" : "Save settings"}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ---------- Payment milestone inline editor ---------- */
function PaymentMilestoneRow({ slug, m, canEdit, onChanged, linkOptions }) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ ...m });
  const dueDateRef = useRef(null);
  const status = PAYMENT_STATUSES.find((s) => s.v === (m.status || "not_due")) || PAYMENT_STATUSES[0];

  const save = async () => {
    setBusy(true);
    // DOM-first read: some native date pickers update the input without firing
    // React onChange, leaving state stale. Normalize to ISO before sending.
    const rawDue = (dueDateRef.current && dueDateRef.current.value) || form.due_date || "";
    const dueIso = rawDue ? normalizeDateInput(rawDue) : null;
    try {
      const body = {
        code: form.code, trigger: form.trigger,
        amount_incl_vat: form.amount_incl_vat === "" ? null : Number(form.amount_incl_vat),
        cumulative_amount: form.cumulative_amount === "" ? null : Number(form.cumulative_amount),
        percentage_main_works: form.percentage_main_works === "" ? null : Number(form.percentage_main_works),
        status: form.status,
        invoice_reference: form.invoice_reference || null,
        due_date: dueIso || (rawDue || null),
        paid_date: form.paid_date || null,
        paid_amount: form.paid_amount === "" ? null : Number(form.paid_amount),
        linked_phase_id: form.linked_phase_id || null,
        linked_task_id: form.linked_task_id || null,
        linked_milestone_id: form.linked_milestone_id || null,
        notes: form.notes || null,
      };
      await portalApi.patchPaymentMilestone(slug, m.id, body);
      setEditing(false);
      onChanged && onChanged();
    } catch { /* ignore */ }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete payment milestone ${m.code || ""}?`)) return;
    try { await portalApi.deletePaymentMilestone(slug, m.id); onChanged && onChanged(); } catch (e) { window.alert(`Could not delete milestone: ${e.message}`); }
  };

  // Lookup linked entity names (used in view-mode display)
  const linkedPhase = linkOptions?.phases?.find((p) => p.id === m.linked_phase_id);
  const linkedTask = linkOptions?.tasks?.find((t) => t.id === m.linked_task_id);
  const linkedMs = linkOptions?.milestones?.find((x) => x.id === m.linked_milestone_id);
  const hasAnyLink = !!(linkedPhase || linkedTask || linkedMs);

  if (editing) {
    const cls = "w-full bg-[#FAF5EC] border border-[#6E1F2C]/20 px-2 py-1 text-[12px]";
    return (
      <>
        <tr data-testid={`portal-payment-row-${m.id}`} className="bg-[#FAF5EC]/40">
          <td className="p-2"><input className={cls} value={form.code || ""} onChange={(e) => setForm({ ...form, code: e.target.value })} /></td>
          <td className="p-2"><input className={cls} value={form.trigger || ""} onChange={(e) => setForm({ ...form, trigger: e.target.value })} /></td>
          <td className="p-2"><input type="number" step="0.01" className={cls} value={form.amount_incl_vat ?? ""} onChange={(e) => setForm({ ...form, amount_incl_vat: e.target.value })} /></td>
          <td className="p-2"><input type="number" step="0.0001" className={cls} value={form.percentage_main_works ?? ""} onChange={(e) => setForm({ ...form, percentage_main_works: e.target.value })} /></td>
          <td className="p-2"><input type="number" step="0.01" className={cls} value={form.cumulative_amount ?? ""} onChange={(e) => setForm({ ...form, cumulative_amount: e.target.value })} /></td>
          <td className="p-2">
            <select className={cls} value={form.status || "not_due"} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              {PAYMENT_STATUSES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
            </select>
          </td>
          <td className="p-2"><input type="date" ref={dueDateRef} data-testid={`portal-payment-due-${m.id}`} className={cls} value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></td>
          <td className="p-2"><input type="number" step="0.01" className={cls} value={form.paid_amount ?? ""} onChange={(e) => setForm({ ...form, paid_amount: e.target.value })} /></td>
          <td className="p-2"><input className={cls} value={form.invoice_reference || ""} onChange={(e) => setForm({ ...form, invoice_reference: e.target.value })} /></td>
          <td className="p-2">
            <div className="flex gap-1">
              <button disabled={busy} onClick={save} data-testid={`portal-payment-save-${m.id}`} className="text-[10px] bg-[#6E1F2C] text-white px-2 py-1 disabled:opacity-50">Save</button>
              <button onClick={() => setEditing(false)} className="text-[10px] text-[#7A6B5F] px-2 py-1">Cancel</button>
            </div>
          </td>
        </tr>
        <tr data-testid={`portal-payment-link-row-${m.id}`} className="bg-[#FAF5EC]/60">
          <td colSpan={10} className="p-2 border-b border-[#6E1F2C]/15">
            <p className="text-[9px] tracking-[0.24em] uppercase text-[#A6822F] mb-2 inline-flex items-center gap-1.5">
              <Link2 size={10} /> Link this payment to site progress
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <div>
                <label className="block text-[9px] tracking-[0.2em] uppercase text-[#7A6B5F] mb-1">Project phase</label>
                <select
                  data-testid={`portal-payment-link-phase-${m.id}`}
                  className={cls}
                  value={form.linked_phase_id || ""}
                  onChange={(e) => setForm({ ...form, linked_phase_id: e.target.value || null })}
                >
                  <option value="">— None —</option>
                  {(linkOptions?.phases || []).map((p) => (
                    <option key={p.id} value={p.id}>{p.name} ({p.progress_percent}%)</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[9px] tracking-[0.2em] uppercase text-[#7A6B5F] mb-1">Gantt activity</label>
                <select
                  data-testid={`portal-payment-link-task-${m.id}`}
                  className={cls}
                  value={form.linked_task_id || ""}
                  onChange={(e) => setForm({ ...form, linked_task_id: e.target.value || null })}
                >
                  <option value="">— None —</option>
                  {(linkOptions?.tasks || []).map((t) => (
                    <option key={t.id} value={t.id}>{(t.name || "").slice(0, 60)}{t.finish_date ? ` · ${t.finish_date}` : ""}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[9px] tracking-[0.2em] uppercase text-[#7A6B5F] mb-1">Project milestone</label>
                <select
                  data-testid={`portal-payment-link-milestone-${m.id}`}
                  className={cls}
                  value={form.linked_milestone_id || ""}
                  onChange={(e) => setForm({ ...form, linked_milestone_id: e.target.value || null })}
                >
                  <option value="">— None —</option>
                  {(linkOptions?.milestones || []).map((x) => (
                    <option key={x.id} value={x.id}>{x.name}{x.target_date ? ` · ${x.target_date}` : ""}</option>
                  ))}
                </select>
              </div>
            </div>
          </td>
        </tr>
      </>
    );
  }

  const paid = Number(m.paid_amount || 0);
  const balance = Number(m.amount_incl_vat || 0) - paid;
  return (
    <tr data-testid={`portal-payment-row-${m.id}`} className="border-b border-[#6E1F2C]/8 hover:bg-[#FAF5EC]/30">
      <td className="p-2 font-medium text-[#6E1F2C]">{m.code || "—"}</td>
      <td className="p-2 text-[12px] text-[#1C110C] max-w-[280px]">
        <span className="line-clamp-2">{m.trigger || "—"}</span>
        {hasAnyLink && (
          <p className="text-[9px] text-[#A6822F] mt-1 inline-flex items-center gap-1">
            <Link2 size={8} />
            {linkedPhase && <span>{linkedPhase.name}</span>}
            {linkedTask && <span className="ml-1">↳ {(linkedTask.name || "").slice(0, 30)}</span>}
            {linkedMs && <span className="ml-1">★ {linkedMs.name}</span>}
          </p>
        )}
      </td>
      <td className="p-2 text-[12px] text-[#1C110C] tabular-nums">{fmtAED(m.amount_incl_vat)}</td>
      <td className="p-2 text-[12px] text-[#7A6B5F] tabular-nums">{fmtPct(m.percentage_main_works)}</td>
      <td className="p-2 text-[12px] text-[#7A6B5F] tabular-nums">{fmtAED(m.cumulative_amount)}</td>
      <td className="p-2"><span className={`text-[9px] tracking-[0.22em] uppercase px-2 py-0.5 ${status.cls}`}>{status.l}</span></td>
      <td className="p-2 text-[12px] text-[#3E2A22]">{m.due_date ? fmtDate(m.due_date) : "—"}</td>
      <td className="p-2 text-[12px] text-[#1C110C] tabular-nums">{paid ? fmtAED(paid) : "—"}</td>
      <td className="p-2 text-[12px] text-[#3E2A22] truncate max-w-[140px]">{m.invoice_reference || "—"}</td>
      <td className="p-2">
        {canEdit && (
          <div className="flex gap-1">
            <button onClick={() => setEditing(true)} data-testid={`portal-payment-edit-${m.id}`} className="p-1.5 border border-[#6E1F2C]/25 text-[#6E1F2C] hover:bg-[#6E1F2C] hover:text-white"><Edit3 size={11} /></button>
            <button onClick={remove} data-testid={`portal-payment-delete-${m.id}`} className="p-1.5 border border-[#A3141A]/40 text-[#A3141A] hover:bg-[#A3141A] hover:text-white"><Trash2 size={11} /></button>
          </div>
        )}
      </td>
    </tr>
  );
}

function StageBreakdown({ stages }) {
  // Group by stage_no/stage_name
  const groups = {};
  stages.forEach((s) => {
    const key = `${s.stage_no ?? "?"}::${s.stage_name || ""}`;
    if (!groups[key]) groups[key] = { stage_no: s.stage_no, stage_name: s.stage_name, items: [], total: 0 };
    groups[key].items.push(s);
    groups[key].total += Number(s.amount_incl_vat || 0);
  });
  const arr = Object.values(groups).sort((a, b) => (a.stage_no || 0) - (b.stage_no || 0));
  if (arr.length === 0) return <p className="text-[12px] text-[#7A6B5F] italic">No stage breakdown imported yet.</p>;
  return (
    <div className="space-y-3" data-testid="portal-financials-stage-list">
      {arr.map((g, i) => (
        <details key={i} className="bg-white border border-[#6E1F2C]/15" data-testid={`portal-financials-stage-${g.stage_no}`}>
          <summary className="cursor-pointer px-4 py-3 flex items-center justify-between hover:bg-[#FAF5EC]/40">
            <div className="flex items-center gap-3 min-w-0">
              <span className="font-serif-display text-base text-[#6E1F2C] shrink-0">Stage {g.stage_no ?? "—"}</span>
              <span className="text-[13px] text-[#1C110C] truncate">{g.stage_name || "(unnamed)"}</span>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <span className="text-[11px] text-[#7A6B5F]">{g.items.length} items</span>
              <span className="font-medium text-[#1C110C] tabular-nums">{fmtAED(g.total)}</span>
              <ChevronRight size={14} className="text-[#7A6B5F]" />
            </div>
          </summary>
          <div className="px-4 pb-3 overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="text-left text-[9px] tracking-[0.2em] uppercase text-[#7A6B5F] border-b border-[#6E1F2C]/10">
                  <th className="py-1.5 pr-2">BOQ #</th>
                  <th className="py-1.5 pr-2">Category</th>
                  <th className="py-1.5 pr-2">Item / Activity</th>
                  <th className="py-1.5 pr-2 text-right">Amount</th>
                  <th className="py-1.5 pr-2">Workflow dependency</th>
                </tr>
              </thead>
              <tbody>
                {g.items.map((it) => (
                  <tr key={it.id} className="border-b border-[#6E1F2C]/5">
                    <td className="py-1.5 pr-2 text-[#7A6B5F]">{it.boq_item || "—"}</td>
                    <td className="py-1.5 pr-2 text-[#3E2A22]">{it.category || "—"}</td>
                    <td className="py-1.5 pr-2 text-[#1C110C]">{it.item_activity || "—"}</td>
                    <td className="py-1.5 pr-2 text-right tabular-nums text-[#1C110C]">{fmtAED(it.amount_incl_vat)}</td>
                    <td className="py-1.5 pr-2 text-[10px] text-[#7A6B5F] max-w-[220px]"><span className="line-clamp-2">{it.workflow_dependency || "—"}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      ))}
    </div>
  );
}

function TrackBadges({ it }) {
  const badge = (ok, labelOk, labelNo) => (
    <span className={`inline-flex items-center text-[9px] tracking-[0.1em] uppercase px-1.5 py-0.5 border ${
      ok ? "text-[#3F6E2C] border-[#3F6E2C]/40 bg-[#3F6E2C]/10" : "text-[#7A6B5F] border-[#7A6B5F]/30"
    }`}>
      {ok ? labelOk : labelNo}
    </span>
  );
  return (
    <div className="flex flex-wrap gap-1">
      {badge(it.payment_made, "Paid", "Unpaid")}
      {badge(it.order_placed, "Ordered", "No order")}
      {badge(it.delivery_received, "Delivered", "Not delivered")}
    </div>
  );
}

function ExternalSupplyTable({ items, canEdit, slug, onChanged, onTrack }) {
  if (!items?.length) return <p className="text-[12px] text-[#7A6B5F] italic">No external supply milestones imported yet.</p>;
  return (
    <div className="overflow-x-auto" data-testid="portal-financials-external-list">
      <table className="w-full text-[12px]">
        <thead>
          <tr className="text-left text-[9px] tracking-[0.2em] uppercase text-[#7A6B5F] border-b border-[#6E1F2C]/15">
            <th className="py-2 pr-3">Item group / Package</th>
            <th className="py-2 pr-3">Vendor / Source</th>
            <th className="py-2 pr-3 text-right">Value</th>
            <th className="py-2 pr-3">Tracking</th>
            <th className="py-2 pr-3">Delivery</th>
            <th className="py-2 pr-3">Pending action</th>
            <th className="py-2 pr-3">Risk if delayed</th>
            {canEdit && <th className="py-2 pr-3"></th>}
          </tr>
        </thead>
        <tbody>
          {items.map((it) => {
            const pendings = [
              it.pending_action_supplier && `Supplier: ${it.pending_action_supplier}`,
              it.pending_action_client && `Client: ${it.pending_action_client}`,
              it.pending_action_designer && `Designer: ${it.pending_action_designer}`,
              it.pending_action_tf && `TF: ${it.pending_action_tf}`,
            ].filter(Boolean);
            const delivery = it.actual_delivery_date
              ? `Received ${it.actual_delivery_date}`
              : (it.scheduled_delivery_date || it.expected_delivery_date
                ? `Expected ${it.scheduled_delivery_date || it.expected_delivery_date}`
                : "—");
            return (
              <tr key={it.id} data-testid={`portal-external-row-${it.id}`} className="border-b border-[#6E1F2C]/8 align-top">
                <td className="py-2 pr-3 text-[#1C110C] font-medium max-w-[220px]">
                  <button
                    type="button"
                    onClick={() => onTrack && onTrack(it)}
                    data-testid={`portal-external-open-${it.id}`}
                    className="text-left hover:text-[#6E1F2C] hover:underline underline-offset-2"
                  >
                    <span className="line-clamp-2">{it.item_group}</span>
                  </button>
                </td>
                <td className="py-2 pr-3 text-[#3E2A22]">{it.vendor_source || "—"}</td>
                <td className="py-2 pr-3 text-right tabular-nums text-[#1C110C]">{fmtAED(it.related_value_incl_vat)}</td>
                <td className="py-2 pr-3"><TrackBadges it={it} /></td>
                <td className="py-2 pr-3 text-[10px] text-[#3E2A22]">{delivery}</td>
                <td className="py-2 pr-3 text-[10px] text-[#A6822F] max-w-[200px]">
                  <span className="line-clamp-3">{pendings.length ? pendings.join(" · ") : "—"}</span>
                </td>
                <td className="py-2 pr-3 text-[10px] text-[#A3141A] max-w-[200px]"><span className="line-clamp-3">{it.risk_if_delayed || "—"}</span></td>
                {canEdit && (
                  <td className="py-2 pr-3">
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => onTrack && onTrack(it)}
                        data-testid={`portal-external-track-${it.id}`}
                        className="px-2 py-1.5 border border-[#6E1F2C]/40 text-[#6E1F2C] hover:bg-[#6E1F2C] hover:text-[#FAF5EC] text-[9px] tracking-[0.16em] uppercase"
                      >
                        Track
                      </button>
                      <button
                        onClick={async () => {
                          if (window.confirm("Delete this external supply row?")) {
                            try { await portalApi.deleteExternalSupply(slug, it.id); onChanged && onChanged(); } catch (e) { window.alert(`Could not delete row: ${e.message}`); }
                          }
                        }}
                        data-testid={`portal-external-delete-${it.id}`}
                        className="p-1.5 border border-[#A3141A]/40 text-[#A3141A] hover:bg-[#A3141A] hover:text-white"
                      >
                        <Trash2 size={11} />
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ---------- Stage Progress vs Value (admin/internal-permitted only) ---------- */
function StageProgressSection({ slug }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    let alive = true;
    portalApi.stageProgressVsValue(slug)
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setErr(e.message); });
    return () => { alive = false; };
  }, [slug]);
  if (err) return null;  // Silently hide for users without access
  if (!data) return null;
  if ((data.rows || []).length === 0) return null;

  const STATUS_TEXT = {
    paid: { l: "Paid", cls: "bg-[#3F6E2C] text-white" },
    partially_paid: { l: "Partial", cls: "bg-[#3E2A22] text-white" },
    invoice_issued: { l: "Invoiced", cls: "bg-[#6E1F2C] text-white" },
    due: { l: "Due", cls: "bg-[#D4A963] text-[#1C110C]" },
    overdue: { l: "Overdue", cls: "bg-[#A3141A] text-white" },
    not_due: { l: "Not Due", cls: "bg-[#7A6B5F] text-white" },
  };
  return (
    <section data-testid="portal-financials-stage-progress">
      <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
        <div>
          <p className="text-[10px] tracking-[0.32em] uppercase text-[#A6822F] inline-flex items-center gap-1.5">
            <Lock size={10} /> Admin-only · derived view
          </p>
          <h3 className="font-serif-display text-xl text-[#1C110C] mt-0.5 inline-flex items-center gap-2">
            <BarChart3 size={16} className="text-[#6E1F2C]" />
            Stage progress vs value
          </h3>
        </div>
        <div className="text-right">
          <p className="text-[10px] tracking-[0.22em] uppercase text-[#7A6B5F]">Overall earned</p>
          <p className="font-serif-display text-lg text-[#6E1F2C]">{data.overall_progress}% · {fmtAED(data.total_earned)}</p>
        </div>
      </div>
      <div className="space-y-2">
        {data.rows.map((r, i) => {
          const earnedPct = r.value ? Math.min(100, (r.earned_value / r.value) * 100) : 0;
          const status = STATUS_TEXT[r.payment_status] || null;
          return (
            <div
              key={i}
              data-testid={`portal-stage-progress-row-${r.stage_no ?? i}`}
              className="bg-white border border-[#6E1F2C]/15 p-3 group hover:border-[#6E1F2C]/35 transition-colors"
            >
              <div className="flex items-center gap-3 flex-wrap mb-2">
                <span className="font-serif-display text-base text-[#6E1F2C]">Stage {r.stage_no ?? "—"}</span>
                <span className="text-[12px] text-[#1C110C] flex-1 min-w-0 truncate">{r.stage_name || "(unnamed)"}</span>
                {r.linked_phase_name && (
                  <span className="text-[9px] tracking-[0.22em] uppercase text-[#7A6B5F] inline-flex items-center gap-1">
                    <Link2 size={9} /> {r.linked_phase_name}
                  </span>
                )}
                {status && (
                  <span className={`text-[9px] tracking-[0.22em] uppercase px-2 py-0.5 ${status.cls}`}>
                    {status.l}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-[11px]">
                <div>
                  <p className="text-[9px] tracking-[0.22em] uppercase text-[#7A6B5F]">Stage value</p>
                  <p className="text-[#1C110C] tabular-nums font-medium">{fmtAED(r.value)}</p>
                </div>
                <div>
                  <p className="text-[9px] tracking-[0.22em] uppercase text-[#7A6B5F]">Progress</p>
                  <p className="text-[#6E1F2C] font-medium">{r.progress_percent}%</p>
                </div>
                <div>
                  <p className="text-[9px] tracking-[0.22em] uppercase text-[#7A6B5F]">Earned</p>
                  <p className="text-[#3F6E2C] tabular-nums font-medium">{fmtAED(r.earned_value)}</p>
                </div>
                <div>
                  <p className="text-[9px] tracking-[0.22em] uppercase text-[#7A6B5F]">Remaining</p>
                  <p className="text-[#A3141A] tabular-nums font-medium">{fmtAED(r.remaining_value)}</p>
                </div>
              </div>
              <div className="h-1.5 bg-[#FAF5EC] mt-3 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#3F6E2C] to-[#A6822F]" style={{ width: `${earnedPct}%` }} />
              </div>
              {r.related_payment_milestones?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {r.related_payment_milestones.map((m) => (
                    <span key={m.id} className="text-[9px] px-2 py-0.5 bg-[#FAF5EC] border border-[#D4A963]/40 text-[#6E1F2C]">
                      {m.code} · {fmtAED(m.amount)}
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* ---------- Variations & additional payments ---------- */
const VARIATION_APPROVALS = [
  { v: "pending", l: "Pending", cls: "bg-[#D4A963] text-[#1C110C]" },
  { v: "approved", l: "Approved", cls: "bg-[#3F6E2C] text-white" },
  { v: "rejected", l: "Rejected", cls: "bg-[#A3141A] text-white" },
  { v: "included_in_contract", l: "Included in Contract", cls: "bg-[#7A6B5F] text-white" },
  { v: "no_extra_charge", l: "No Extra Charge", cls: "bg-[#1C110C] text-white" },
];

function VariationModal({ slug, variation, onClose, onSaved }) {
  const [form, setForm] = useState({
    code: variation?.code ?? "",
    title: variation?.title ?? "",
    description: variation?.description ?? "",
    quotation_ref: variation?.quotation_ref ?? "",
    approval_status: variation?.approval_status ?? "pending",
    amount_incl_vat: variation?.amount_incl_vat ?? "",
    payment_status: variation?.payment_status ?? "not_due",
    paid_amount: variation?.paid_amount ?? "",
    due_date: variation?.due_date ?? "",
    invoice_reference: variation?.invoice_reference ?? "",
    notes: variation?.notes ?? "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const cls = "w-full bg-[#FAF5EC] border border-[#6E1F2C]/20 px-3 py-2 text-[#1C110C] text-sm focus:outline-none focus:border-[#6E1F2C]";
  const lbl = "block text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] mb-1";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const body = {
        code: form.code || null,
        title: form.title || null,
        description: form.description || null,
        quotation_ref: form.quotation_ref || null,
        approval_status: form.approval_status,
        amount_incl_vat: form.amount_incl_vat === "" ? null : Number(form.amount_incl_vat),
        payment_status: form.payment_status,
        paid_amount: form.paid_amount === "" ? null : Number(form.paid_amount),
        due_date: form.due_date || null,
        invoice_reference: form.invoice_reference || null,
        notes: form.notes || null,
      };
      if (variation?.id) await portalApi.patchVariation(slug, variation.id, body);
      else await portalApi.createVariation(slug, body);
      onSaved && onSaved();
      onClose();
    } catch (e2) { setErr(e2.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-[#1C110C]/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <form onSubmit={submit} data-testid="portal-variation-modal" className="bg-white w-full max-w-[680px] border border-[#6E1F2C]/20 my-6" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-[#6E1F2C]/10 flex items-center justify-between bg-gradient-to-r from-white to-[#FAF5EC]">
          <h3 className="font-serif-display text-2xl text-[#1C110C]">{variation?.id ? "Edit variation" : "Add variation"}</h3>
          <button type="button" onClick={onClose} className="text-[#7A6B5F] hover:text-[#1C110C]"><X size={20} /></button>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={lbl}>Reference / code</label>
            <input className={cls} value={form.code} data-testid="portal-variation-field-code" onChange={(e) => setForm((s) => ({ ...s, code: e.target.value }))} placeholder="e.g. EST-000860" />
          </div>
          <div>
            <label className={lbl}>Quotation / reference</label>
            <input className={cls} value={form.quotation_ref} data-testid="portal-variation-field-quotation" onChange={(e) => setForm((s) => ({ ...s, quotation_ref: e.target.value }))} />
          </div>
          <div className="md:col-span-2">
            <label className={lbl}>Title / description</label>
            <input className={cls} value={form.title} data-testid="portal-variation-field-title" onChange={(e) => setForm((s) => ({ ...s, title: e.target.value }))} />
          </div>
          <div className="md:col-span-2">
            <label className={lbl}>Detail (optional)</label>
            <textarea rows={2} className={cls} value={form.description} data-testid="portal-variation-field-description" onChange={(e) => setForm((s) => ({ ...s, description: e.target.value }))} />
          </div>
          <div>
            <label className={lbl}>Approval status</label>
            <select className={cls} value={form.approval_status} data-testid="portal-variation-field-approval" onChange={(e) => setForm((s) => ({ ...s, approval_status: e.target.value }))}>
              {VARIATION_APPROVALS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
          </div>
          <div>
            <label className={lbl}>Amount incl. VAT (AED) — leave empty if unpriced</label>
            <input type="number" step="0.01" min="0" className={cls} value={form.amount_incl_vat} data-testid="portal-variation-field-amount" onChange={(e) => setForm((s) => ({ ...s, amount_incl_vat: e.target.value }))} />
          </div>
          <div>
            <label className={lbl}>Payment status</label>
            <select className={cls} value={form.payment_status} data-testid="portal-variation-field-payment-status" onChange={(e) => setForm((s) => ({ ...s, payment_status: e.target.value }))}>
              {PAYMENT_STATUSES.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
          </div>
          <div>
            <label className={lbl}>Paid amount (tracking only)</label>
            <input type="number" step="0.01" min="0" className={cls} value={form.paid_amount} data-testid="portal-variation-field-paid" onChange={(e) => setForm((s) => ({ ...s, paid_amount: e.target.value }))} />
          </div>
          <div>
            <label className={lbl}>Due date (optional)</label>
            <input type="date" className={cls} value={form.due_date || ""} data-testid="portal-variation-field-due" onChange={(e) => setForm((s) => ({ ...s, due_date: e.target.value }))} />
          </div>
          <div>
            <label className={lbl}>Invoice / reference</label>
            <input className={cls} value={form.invoice_reference} data-testid="portal-variation-field-invoice" onChange={(e) => setForm((s) => ({ ...s, invoice_reference: e.target.value }))} />
          </div>
          <div className="md:col-span-2">
            <label className={lbl}>Notes</label>
            <textarea rows={2} className={cls} value={form.notes} data-testid="portal-variation-field-notes" onChange={(e) => setForm((s) => ({ ...s, notes: e.target.value }))} />
          </div>
          {err && <p className="md:col-span-2 text-[11px] text-[#A3141A]" data-testid="portal-variation-error">{err}</p>}
        </div>
        <div className="px-6 py-4 border-t border-[#6E1F2C]/10 flex items-center justify-end gap-2 bg-[#FAF5EC]/40">
          <button type="button" onClick={onClose} className="text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] px-4 py-2">Cancel</button>
          <button type="submit" disabled={busy} data-testid="portal-variation-save" className="bg-[#6E1F2C] text-[#FAF5EC] hover:bg-[#56151F] px-5 py-2.5 text-[10px] tracking-[0.28em] uppercase disabled:opacity-60">
            {busy ? "Saving…" : "Save variation"}
          </button>
        </div>
      </form>
    </div>
  );
}

function VariationsSection({ slug, variations, varSummary, canEdit, summary, onChanged }) {
  const [modal, setModal] = useState(null); // null | {} (new) | variation
  const [showArchived, setShowArchived] = useState(false);
  const active = variations.filter((v) => !v.archived);
  const archived = variations.filter((v) => v.archived);
  const rows = showArchived ? [...active, ...archived] : active;
  const showReclass = canEdit && slug === "4601-palm-tower" && !varSummary?.has_rows && Number(summary?.approved_variations || 0) > 0;

  const voidRow = async (v) => {
    if (!window.confirm(`Void variation ${v.code || v.title || ""}? It is archived (reversible), never deleted.`)) return;
    try { await portalApi.archiveVariation(slug, v.id); onChanged && onChanged(); } catch (e) { window.alert(e.message); }
  };
  const restoreRow = async (v) => {
    try { await portalApi.restoreVariation(slug, v.id); onChanged && onChanged(); } catch (e) { window.alert(e.message); }
  };

  return (
    <section data-testid="portal-financials-variations">
      <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
        <div>
          <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C]">Variations & additional payments</p>
          <h3 className="font-serif-display text-xl text-[#1C110C]">Separate from the base contract schedule</h3>
        </div>
        <div className="flex items-center gap-2">
          {archived.length > 0 && (
            <button type="button" onClick={() => setShowArchived((s) => !s)} data-testid="portal-variations-show-archived" className="text-[10px] tracking-[0.22em] uppercase text-[#7A6B5F] border border-[#6E1F2C]/20 px-3 py-2 hover:text-[#1C110C]">
              {showArchived ? "Hide" : "Show"} archived ({archived.length})
            </button>
          )}
          {canEdit && (
            <button type="button" onClick={() => setModal({})} data-testid="portal-variations-add-btn" className="inline-flex items-center gap-2 bg-[#6E1F2C] text-[#FAF5EC] px-3 py-2 text-[10px] tracking-[0.28em] uppercase hover:bg-[#56151F]">
              <Plus size={13} /> Add variation
            </button>
          )}
        </div>
      </div>

      {varSummary && (
        <div className="grid grid-cols-3 gap-3 mb-4" data-testid="portal-variations-summary">
          <StatCard testid="portal-variations-approved-total" label="Approved variations" value={fmtAED(varSummary.approved_total)} accent="maroon" />
          <StatCard testid="portal-variations-paid-total" label="Paid (tracking)" value={fmtAED(varSummary.paid_total)} accent="green" />
          <StatCard testid="portal-variations-outstanding" label="Variations outstanding" value={fmtAED(varSummary.outstanding)} accent="red" />
        </div>
      )}

      {showReclass && (
        <div className="bg-[#FAF5EC] border border-[#D4A963]/50 p-3 mb-4 flex items-start justify-between gap-3 text-[11px] text-[#3E2A22]" data-testid="portal-variations-reclass-banner">
          <p>
            The approved variation <strong>EST-000860 · {fmtAED(summary.approved_variations)}</strong> is currently recorded only in the summary.
            Reclassify it into this section once — the total stays {fmtAED(summary.approved_variations)}, nothing is added on top.
          </p>
          <button
            type="button"
            data-testid="portal-variations-reclass-btn"
            onClick={async () => {
              try { await portalApi.reclassPalmVariation(); onChanged && onChanged(); } catch (e) { window.alert(e.message); }
            }}
            className="shrink-0 bg-[#6E1F2C] text-[#FAF5EC] px-3 py-2 text-[10px] tracking-[0.22em] uppercase hover:bg-[#56151F]"
          >
            Reclassify once
          </button>
        </div>
      )}

      {rows.length === 0 ? (
        <p className="text-[12px] text-[#7A6B5F] italic">No variations recorded yet.</p>
      ) : (
        <div className="overflow-x-auto bg-white border border-[#6E1F2C]/15">
          <table className="w-full">
            <thead>
              <tr className="text-left text-[9px] tracking-[0.2em] uppercase text-[#7A6B5F] border-b border-[#6E1F2C]/15">
                <th className="py-2 px-2">Ref</th>
                <th className="py-2 px-2">Title / description</th>
                <th className="py-2 px-2">Quotation</th>
                <th className="py-2 px-2">Approval</th>
                <th className="py-2 px-2">Amount</th>
                <th className="py-2 px-2">Payment</th>
                <th className="py-2 px-2">Paid</th>
                <th className="py-2 px-2">Balance</th>
                <th className="py-2 px-2">Due</th>
                <th className="py-2 px-2">Invoice</th>
                {canEdit && <th className="py-2 px-2"></th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((v) => {
                const appr = VARIATION_APPROVALS.find((o) => o.v === v.approval_status) || VARIATION_APPROVALS[0];
                const pay = PAYMENT_STATUSES.find((o) => o.v === (v.payment_status || "not_due")) || PAYMENT_STATUSES[0];
                return (
                  <tr key={v.id} data-testid={`portal-variation-row-${v.id}`} className={`border-b border-[#6E1F2C]/8 ${v.archived ? "opacity-50" : "hover:bg-[#FAF5EC]/30"}`}>
                    <td className="p-2 font-medium text-[#6E1F2C] whitespace-nowrap">{v.code || "—"}{v.archived && <span className="block text-[8px] tracking-[0.2em] uppercase text-[#A3141A]">Voided</span>}</td>
                    <td className="p-2 text-[12px] text-[#1C110C] max-w-[260px]">
                      <span className="line-clamp-2">{v.title || "—"}</span>
                      {v.notes && <span className="block text-[10px] text-[#7A6B5F] line-clamp-2 whitespace-pre-line">{v.notes}</span>}
                    </td>
                    <td className="p-2 text-[11px] text-[#3E2A22] whitespace-nowrap">{v.quotation_ref || "—"}</td>
                    <td className="p-2"><span className={`text-[9px] tracking-[0.18em] uppercase px-2 py-0.5 whitespace-nowrap ${appr.cls}`}>{appr.l}</span></td>
                    <td className="p-2 text-[12px] text-[#1C110C] tabular-nums whitespace-nowrap">{v.amount_incl_vat == null ? <span className="italic text-[#7A6B5F]">Unpriced</span> : fmtAED(v.amount_incl_vat)}</td>
                    <td className="p-2"><span className={`text-[9px] tracking-[0.18em] uppercase px-2 py-0.5 whitespace-nowrap ${pay.cls}`}>{pay.l}</span></td>
                    <td className="p-2 text-[12px] tabular-nums whitespace-nowrap">{v.paid_amount != null ? fmtAED(v.paid_amount) : "—"}</td>
                    <td className="p-2 text-[12px] tabular-nums whitespace-nowrap">{v.balance_amount != null ? fmtAED(v.balance_amount) : "—"}</td>
                    <td className="p-2 text-[12px] text-[#3E2A22] whitespace-nowrap">{v.due_date ? fmtDate(v.due_date) : "—"}</td>
                    <td className="p-2 text-[12px] text-[#3E2A22] truncate max-w-[120px]">{v.invoice_reference || "—"}</td>
                    {canEdit && (
                      <td className="p-2">
                        <div className="flex gap-1">
                          <button onClick={() => setModal(v)} data-testid={`portal-variation-edit-${v.id}`} className="p-1.5 border border-[#6E1F2C]/25 text-[#6E1F2C] hover:bg-[#6E1F2C] hover:text-white"><Edit3 size={11} /></button>
                          {v.archived ? (
                            <button onClick={() => restoreRow(v)} data-testid={`portal-variation-restore-${v.id}`} className="px-2 py-1 border border-[#3F6E2C]/40 text-[#3F6E2C] text-[9px] tracking-[0.14em] uppercase hover:bg-[#3F6E2C] hover:text-white">Restore</button>
                          ) : (
                            <button onClick={() => voidRow(v)} data-testid={`portal-variation-void-${v.id}`} className="px-2 py-1 border border-[#A3141A]/40 text-[#A3141A] text-[9px] tracking-[0.14em] uppercase hover:bg-[#A3141A] hover:text-white">Void</button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-[10px] text-[#7A6B5F] mt-2 italic" data-testid="portal-variations-footnote">
        Pending, unpriced, rejected, included-in-contract and no-extra-charge items never increase approved totals.
        Paid amounts here are tracking only — they never create received-money ledger entries. Phase 1 advance credit is never applied to variations.
      </p>
      {modal && (
        <VariationModal slug={slug} variation={modal.id ? modal : null} onClose={() => setModal(null)} onSaved={onChanged} />
      )}
    </section>
  );
}

/* ---------- Credit application breakdown ---------- */
function CreditApplicationModal({ slug, credit, onClose, onSaved }) {
  const [form, setForm] = useState({
    contract_source: credit?.contract_source ?? "",
    original_credit: credit?.original_credit ?? "",
    receiving_milestone_code: credit?.receiving_milestone_code ?? "",
    original_instalment: credit?.original_instalment ?? "",
    applied_credit: credit?.applied_credit ?? "",
    separate_payment_note: credit?.separate_payment_note ?? "",
    notes: credit?.notes ?? "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const cls = "w-full bg-[#FAF5EC] border border-[#6E1F2C]/20 px-3 py-2 text-[#1C110C] text-sm focus:outline-none focus:border-[#6E1F2C]";
  const lbl = "block text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] mb-1";
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const body = {
        contract_source: form.contract_source || null,
        original_credit: form.original_credit === "" ? null : Number(form.original_credit),
        receiving_milestone_code: form.receiving_milestone_code || null,
        original_instalment: form.original_instalment === "" ? null : Number(form.original_instalment),
        applied_credit: form.applied_credit === "" ? null : Number(form.applied_credit),
        separate_payment_note: form.separate_payment_note || null,
        notes: form.notes || null,
      };
      await portalApi.patchCreditApplication(slug, body);
      onSaved && onSaved();
      onClose();
    } catch (e2) { setErr(e2.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-[#1C110C]/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <form onSubmit={submit} data-testid="portal-credit-modal" className="bg-white w-full max-w-[560px] border border-[#6E1F2C]/20 my-6" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-[#6E1F2C]/10 flex items-center justify-between bg-gradient-to-r from-white to-[#FAF5EC]">
          <h3 className="font-serif-display text-2xl text-[#1C110C]">Credit application breakdown</h3>
          <button type="button" onClick={onClose} className="text-[#7A6B5F] hover:text-[#1C110C]"><X size={20} /></button>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className={lbl}>Contract / source</label>
            <input className={cls} value={form.contract_source} data-testid="portal-credit-field-source" onChange={(e) => setForm((s) => ({ ...s, contract_source: e.target.value }))} />
          </div>
          <div>
            <label className={lbl}>Original advance credit (AED)</label>
            <input type="number" step="0.01" min="0" className={cls} value={form.original_credit} data-testid="portal-credit-field-original" onChange={(e) => setForm((s) => ({ ...s, original_credit: e.target.value }))} />
          </div>
          <div>
            <label className={lbl}>Receiving milestone</label>
            <input className={cls} value={form.receiving_milestone_code} data-testid="portal-credit-field-milestone" onChange={(e) => setForm((s) => ({ ...s, receiving_milestone_code: e.target.value }))} placeholder="e.g. M4(ii)" />
          </div>
          <div>
            <label className={lbl}>Original instalment (AED)</label>
            <input type="number" step="0.01" min="0" className={cls} value={form.original_instalment} data-testid="portal-credit-field-instalment" onChange={(e) => setForm((s) => ({ ...s, original_instalment: e.target.value }))} />
          </div>
          <div>
            <label className={lbl}>Applied credit (AED)</label>
            <input type="number" step="0.01" min="0" className={cls} value={form.applied_credit} data-testid="portal-credit-field-applied" onChange={(e) => setForm((s) => ({ ...s, applied_credit: e.target.value }))} />
          </div>
          <div className="md:col-span-2">
            <label className={lbl}>Separate-payment note</label>
            <textarea rows={2} className={cls} value={form.separate_payment_note} data-testid="portal-credit-field-note" onChange={(e) => setForm((s) => ({ ...s, separate_payment_note: e.target.value }))} />
          </div>
          <div className="md:col-span-2">
            <label className={lbl}>Notes</label>
            <textarea rows={2} className={cls} value={form.notes} data-testid="portal-credit-field-notes" onChange={(e) => setForm((s) => ({ ...s, notes: e.target.value }))} />
          </div>
          {err && <p className="md:col-span-2 text-[11px] text-[#A3141A]" data-testid="portal-credit-error">{err}</p>}
        </div>
        <div className="px-6 py-4 border-t border-[#6E1F2C]/10 flex items-center justify-end gap-2 bg-[#FAF5EC]/40">
          <button type="button" onClick={onClose} className="text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] px-4 py-2">Cancel</button>
          <button type="submit" disabled={busy} data-testid="portal-credit-save" className="bg-[#6E1F2C] text-[#FAF5EC] hover:bg-[#56151F] px-5 py-2.5 text-[10px] tracking-[0.28em] uppercase disabled:opacity-60">
            {busy ? "Saving…" : "Save breakdown"}
          </button>
        </div>
      </form>
    </div>
  );
}

function CreditApplicationCard({ slug, credit, canEdit, onChanged }) {
  const [showEdit, setShowEdit] = useState(false);
  if (!credit && !canEdit) return null;
  const Field = ({ label, children, testid }) => (
    <div data-testid={testid}>
      <p className="text-[9px] tracking-[0.24em] uppercase text-[#7A6B5F]">{label}</p>
      <p className="text-[13px] text-[#1C110C] font-medium tabular-nums mt-0.5">{children}</p>
    </div>
  );
  return (
    <section data-testid="portal-financials-credit-card">
      <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
        <div>
          <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C]">Credit application</p>
          <h3 className="font-serif-display text-xl text-[#1C110C]">Advance credit breakdown</h3>
        </div>
        {canEdit && (
          <button type="button" onClick={() => setShowEdit(true)} data-testid="portal-credit-edit-btn" className="inline-flex items-center gap-2 border border-[#6E1F2C] text-[#6E1F2C] px-3 py-2 text-[10px] tracking-[0.28em] uppercase hover:bg-[#6E1F2C] hover:text-white">
            <Edit3 size={12} /> {credit ? "Edit" : "Add breakdown"}
          </button>
        )}
      </div>
      {!credit ? (
        <p className="text-[12px] text-[#7A6B5F] italic">No credit application recorded for this project.</p>
      ) : (
        <div className="bg-white border border-[#D4A963]/40 p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-4">
            <div className="col-span-2 md:col-span-4">
              <p className="text-[9px] tracking-[0.24em] uppercase text-[#7A6B5F]">Contract / source</p>
              <p className="text-[13px] text-[#1C110C] font-medium mt-0.5">{credit.contract_source || "—"}</p>
            </div>
            <Field label="Original advance credit" testid="portal-credit-original">{fmtAED(credit.original_credit)}</Field>
            <Field label="Receiving milestone" testid="portal-credit-milestone">{credit.receiving_milestone_code || "—"}</Field>
            <Field label="Original instalment" testid="portal-credit-instalment">{fmtAED(credit.original_instalment)}</Field>
            <Field label="Applied credit" testid="portal-credit-applied">{fmtAED(credit.applied_credit)}</Field>
            <Field label="Net instalment" testid="portal-credit-net">{fmtAED(credit.net_instalment)}</Field>
            <Field label="Remaining credit" testid="portal-credit-remaining">{fmtAED(credit.remaining_credit)}</Field>
          </div>
          <div className="mt-4 pt-3 border-t border-[#6E1F2C]/10 space-y-1 text-[11px] text-[#3E2A22]">
            {credit.separate_payment_note && (
              <p data-testid="portal-credit-separate-note" className="whitespace-pre-line">{credit.separate_payment_note}</p>
            )}
            <p data-testid="portal-credit-policy-note" className="text-[#7A6B5F]">
              The advance credit is applied once, to {credit.receiving_milestone_code || "the receiving milestone"} only —
              later milestones receive AED 0.00 credit. Variations are billed separately and never consume this credit.
            </p>
            {credit.notes && <p className="whitespace-pre-line">{credit.notes}</p>}
          </div>
        </div>
      )}
      {showEdit && (
        <CreditApplicationModal slug={slug} credit={credit} onClose={() => setShowEdit(false)} onSaved={onChanged} />
      )}
    </section>
  );
}

/* ---------- Main tab ---------- */
export function FinancialsTab({ slug }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [linkOptions, setLinkOptions] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [trackPkg, setTrackPkg] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let alive = true;
    portalApi.getFinancials(slug)
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setErr(e.message); });
    portalApi.financialsLinkOptions(slug)
      .then((d) => { if (alive) setLinkOptions(d); })
      .catch(() => { /* 403 for client viewers — ignore */ });
    return () => { alive = false; };
  }, [slug, refreshKey]);
  const reload = () => setRefreshKey((k) => k + 1);

  if (err) {
    return (
      <div data-testid="portal-financials-error" className="bg-white border border-[#A3141A]/30 p-6 text-center">
        <Lock className="mx-auto text-[#A3141A] mb-3" size={26} />
        <p className="font-serif-display text-lg text-[#1C110C]">{err}</p>
        <p className="text-[11px] text-[#7A6B5F] mt-2">Financials are restricted by default. Contact the Main Admin if you need access.</p>
      </div>
    );
  }
  if (!data) return <div className="text-[11px] text-[#7A6B5F] py-6">Loading financials…</div>;

  const canEdit = !!data.viewer_can_edit;
  const canView = data.viewer_can_view !== false;
  const summary = data.summary || {};
  const payments = data.payment_milestones || [];
  const stages = data.stage_details || [];
  const external = data.external_supply || [];
  const visibility = data.visibility || {};
  const variations = data.variations || [];
  const varSummary = data.variations_summary || null;
  const credit = data.credit_application || null;

  // Progress metrics — backend is the source of truth (received / configured base)
  const totalValue = Number(summary.total_project_value || summary.main_works_value || 0);
  const received = Number(summary.amount_received || 0);
  const invoiced = Number(summary.amount_invoiced || 0);
  const progressBase = summary.payment_progress_base === "total_value" && totalValue
    ? totalValue
    : Number(summary.main_works_value || totalValue || 0);
  const paymentProgress = summary.payment_progress_percent != null
    ? Number(summary.payment_progress_percent)
    : (progressBase ? (received / progressBase) * 100 : 0);
  const invoicedProgress = progressBase ? (invoiced / progressBase) * 100 : 0;
  const progressBaseLabel = summary.payment_progress_base === "total_value" ? "total project value" : "main works";

  // For non-admin viewers we received a stripped payload — show only shared sections
  const limitedView = !canView;

  // Contractor-side team without financial permission: show the package
  // status list only (no amounts, no invoices, no summary).
  if (limitedView) {
    return (
      <div data-testid="portal-financials-tab" className="space-y-6 pb-24 lg:pb-0">
        <div>
          <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C] inline-flex items-center gap-1.5">
            <Lock size={10} /> Restricted view
          </p>
          <h2 className="font-serif-display text-2xl text-[#1C110C] mt-1">External packages</h2>
          <p className="text-[11px] text-[#7A6B5F] mt-1 italic">
            You do not have permission to view financial values on this project. Only supplier/package status is shown below. Contact the Main Admin if you need financial access.
          </p>
        </div>
        {external.length === 0 ? (
          <div className="bg-white border border-[#6E1F2C]/15 p-6 text-center text-[11px] text-[#7A6B5F]">
            No external supply packages recorded yet.
          </div>
        ) : (
          <div className="bg-white border border-[#6E1F2C]/15 overflow-x-auto" data-testid="portal-packages-status-list">
            <table className="w-full text-sm">
              <thead className="bg-[#FAF5EC] text-[10px] tracking-[0.18em] uppercase text-[#7A6B5F]">
                <tr>
                  <th className="text-left px-4 py-3">Package</th>
                  <th className="text-left px-4 py-3">Vendor / source</th>
                  <th className="text-left px-4 py-3">Required on site</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Delivery</th>
                </tr>
              </thead>
              <tbody>
                {external.map((e) => (
                  <tr key={e.id} className="border-t border-[#6E1F2C]/10">
                    <td className="px-4 py-3 text-[#1C110C]">{e.item_group || "—"}</td>
                    <td className="px-4 py-3 text-[#3E2A22]">{e.vendor_source || "—"}</td>
                    <td className="px-4 py-3 text-[#3E2A22]">{e.required_on_site_stage || "—"}</td>
                    <td className="px-4 py-3 text-[10px] uppercase tracking-[0.18em] text-[#6E1F2C]">{e.status || "—"}</td>
                    <td className="px-4 py-3 text-[10px] text-[#3E2A22]">
                      {e.delivered_status && <p>{e.delivered_status}</p>}
                      {e.expected_delivery_date && <p className="text-[#7A6B5F]">Expected {e.expected_delivery_date}</p>}
                      {e.actual_delivery_date && <p className="text-[#7A6B5F]">Actual {e.actual_delivery_date}</p>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-testid="portal-financials-tab" className="space-y-8 pb-24 lg:pb-0">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C] inline-flex items-center gap-1.5">
            <Lock size={10} /> Admin only by default
          </p>
          <h2 className="font-serif-display text-2xl text-[#1C110C] mt-1">Financials</h2>
          {limitedView && (
            <p className="text-[11px] text-[#7A6B5F] mt-1 italic">
              You are viewing only the sections the Main Admin has explicitly shared with you.
            </p>
          )}
        </div>
        {canEdit && (
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={async () => {
                const c1 = window.confirm(
                  "Reset Financial Data?\n\nThis clears ONLY this project's financial module: payment milestones, stage value breakdown, external supply rows and derived summary values.\n\nDaily updates, photos, reports, comments, files, programme tasks and audit history will NOT be modified."
                );
                if (!c1) return;
                const c2 = window.confirm("Are you sure? This cannot be undone. You can re-import the Excel afterwards.");
                if (!c2) return;
                try {
                  const r = await portalApi.resetFinancials(slug);
                  window.alert(
                    `Financial data reset.\nDeleted: ${r.deleted.payment_milestones} milestones, ${r.deleted.stage_details} stage rows, ${r.deleted.external_supply} external supply rows.\n\n${r.safeguard}`
                  );
                  reload();
                } catch (e) { window.alert(`Reset failed: ${e.message}`); }
              }}
              data-testid="portal-financials-reset-btn"
              className="inline-flex items-center gap-2 border border-[#A3141A]/60 text-[#A3141A] px-3 py-2 text-[10px] tracking-[0.28em] uppercase hover:bg-[#A3141A] hover:text-white"
            >
              <Trash2 size={13} /> Reset Financial Data
            </button>
            <button
              onClick={() => setShowSettings(true)}
              data-testid="portal-financials-settings-btn"
              className="inline-flex items-center gap-2 border border-[#6E1F2C] text-[#6E1F2C] px-3 py-2 text-[10px] tracking-[0.28em] uppercase hover:bg-[#6E1F2C] hover:text-white"
            >
              <Settings size={13} /> Settings
            </button>
            <button
              onClick={() => setShowUpload(true)}
              data-testid="portal-financials-upload-btn"
              className="inline-flex items-center gap-2 bg-[#6E1F2C] text-[#FAF5EC] px-3 py-2 text-[10px] tracking-[0.28em] uppercase hover:bg-[#56151F]"
            >
              <Upload size={13} /> Upload payment Excel
            </button>
          </div>
        )}
      </div>

      {/* Summary cards */}
      {summary && Object.values(summary).some((v) => v != null && v !== "") ? (
        <section data-testid="portal-financials-summary">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard testid="portal-financials-stat-total" label="Total project value" value={fmtAED(summary.total_project_value)} accent="maroon" lock={canEdit} />
            <StatCard testid="portal-financials-stat-main" label="Main works" value={fmtAED(summary.main_works_value)} accent="gold" lock={canEdit} />
            <StatCard testid="portal-financials-stat-specialized" label="Specialized supply" value={fmtAED(summary.specialized_supply_value)} accent="gold" lock={canEdit} />
            <StatCard testid="portal-financials-stat-variations" label="Approved variations" value={fmtAED(summary.approved_variations)} accent="maroon" lock={canEdit} />
            <StatCard testid="portal-financials-stat-invoiced" label="Amount invoiced" value={fmtAED(summary.amount_invoiced)} accent="maroon" lock={canEdit} />
            <StatCard testid="portal-financials-stat-received" label="Amount received" value={fmtAED(summary.amount_received)} accent="green" lock={canEdit} />
            <StatCard testid="portal-financials-stat-balance" label="Balance pending" value={fmtAED(summary.balance_pending != null ? summary.balance_pending : (progressBase - received))} accent="red" lock={canEdit} />
            <StatCard testid="portal-financials-stat-retention" label="Retention" value={`${fmtAED(summary.retention_amount)}${summary.retention_percentage ? ` · ${fmtPct(summary.retention_percentage)}` : ""}`} accent="maroon" lock={canEdit} />
            {varSummary?.combined_total_value != null && (
              <StatCard testid="portal-financials-stat-combined-total" label="Total incl. variations" value={fmtAED(varSummary.combined_total_value)} hint="Base contract + approved variations" accent="gold" lock={canEdit} />
            )}
            {varSummary?.combined_outstanding != null && (
              <StatCard testid="portal-financials-stat-combined-outstanding" label="Outstanding incl. variations" value={fmtAED(varSummary.combined_outstanding)} hint="Base balance + variations outstanding" accent="red" lock={canEdit} />
            )}
          </div>
          {totalValue > 0 && (
            <div className="mt-5 bg-white border border-[#6E1F2C]/15 p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F]">Payment progress (received / {progressBaseLabel})</p>
                <p className="font-serif-display text-xl text-[#6E1F2C]">{paymentProgress.toFixed(1)}%</p>
              </div>
              <ProgressBar pct={paymentProgress} />
              <div className="mt-3 flex items-center justify-between text-[10px] text-[#7A6B5F]">
                <span>Invoiced: <span className="text-[#1C110C]">{fmtAED(invoiced)}</span> ({invoicedProgress.toFixed(1)}%)</span>
                <span>Last update: {summary.last_payment_update ? fmtDate(summary.last_payment_update) : "—"}</span>
              </div>
            </div>
          )}
          {summary.financial_notes && canEdit && (
            <div className="mt-4 bg-[#FAF5EC] border border-[#D4A963]/40 p-3 text-[12px] text-[#3E2A22]">
              <p className="text-[9px] tracking-[0.22em] uppercase text-[#A6822F] mb-1 inline-flex items-center gap-1"><Lock size={9} /> Internal notes</p>
              <p className="whitespace-pre-line">{summary.financial_notes}</p>
            </div>
          )}
          {data.client_notes && (
            <div className="mt-4 bg-white border border-[#6E1F2C]/15 p-3 text-[12px] text-[#3E2A22]">
              <p className="text-[9px] tracking-[0.22em] uppercase text-[#6E1F2C] mb-1">Client-shared notes</p>
              <p className="whitespace-pre-line">{data.client_notes}</p>
            </div>
          )}
        </section>
      ) : (
        canEdit && (
          <div className="bg-white border border-dashed border-[#6E1F2C]/25 p-10 text-center">
            <DollarSign className="mx-auto text-[#D4A963] mb-3" size={32} />
            <p className="text-sm text-[#1C110C]">No financial data set up yet.</p>
            <p className="text-[11px] text-[#7A6B5F] mt-1">Upload a payment milestone Excel above, or click Settings to enter values manually.</p>
          </div>
        )
      )}

      {/* Payment milestones */}
      {(canEdit || (data.payment_milestones && data.payment_milestones.length > 0)) && (
        <section data-testid="portal-financials-payment-milestones">
          <div className="flex items-end justify-between mb-3">
            <div>
              <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C]">Payment milestones</p>
              <h3 className="font-serif-display text-xl text-[#1C110C]">Schedule & payment status</h3>
            </div>
            <p className="text-[11px] text-[#7A6B5F]">{payments.length} milestone{payments.length === 1 ? "" : "s"}</p>
          </div>
          {payments.length === 0 ? (
            <p className="text-[12px] text-[#7A6B5F] italic">No payment milestones yet. Upload an Excel above or add manually.</p>
          ) : (
            <div className="overflow-x-auto bg-white border border-[#6E1F2C]/15">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-[9px] tracking-[0.2em] uppercase text-[#7A6B5F] border-b border-[#6E1F2C]/15">
                    <th className="py-2 px-2">Code</th>
                    <th className="py-2 px-2">Trigger / stage</th>
                    <th className="py-2 px-2">Amount</th>
                    <th className="py-2 px-2">% works</th>
                    <th className="py-2 px-2">Cumulative</th>
                    <th className="py-2 px-2">Status</th>
                    <th className="py-2 px-2">Due</th>
                    <th className="py-2 px-2">Paid</th>
                    <th className="py-2 px-2">Invoice</th>
                    {canEdit && <th className="py-2 px-2"></th>}
                  </tr>
                </thead>
                <tbody>
                  {payments.map((m) => (
                    <PaymentMilestoneRow key={m.id} slug={slug} m={m} canEdit={canEdit} onChanged={reload} linkOptions={linkOptions} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* Variations & additional payments — separately headed, under the base schedule */}
      {(canEdit || variations.length > 0) && (
        <VariationsSection
          slug={slug}
          variations={variations}
          varSummary={varSummary}
          canEdit={canEdit}
          summary={summary}
          onChanged={reload}
        />
      )}

      {/* Credit application breakdown */}
      {(canEdit || credit) && (
        <CreditApplicationCard slug={slug} credit={credit} canEdit={canEdit} onChanged={reload} />
      )}

      {/* Stage breakdown */}
      {(canEdit || (data.stage_details && data.stage_details.length > 0)) && (
        <section>
          <div className="flex items-end justify-between mb-3">
            <div>
              <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C]">Stage value breakdown</p>
              <h3 className="font-serif-display text-xl text-[#1C110C]">BOQ items grouped by stage</h3>
            </div>
            <p className="text-[11px] text-[#7A6B5F]">{stages.length} line items</p>
          </div>
          <StageBreakdown stages={stages} />
        </section>
      )}

      {/* Stage Progress vs Value (derived, admin-only) */}
      {canEdit && <StageProgressSection slug={slug} />}

      {/* External supply */}
      {(canEdit || (data.external_supply && data.external_supply.length > 0)) && (
        <section>
          <div className="flex items-end justify-between mb-3">
            <div>
              <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C]">External supply / client-nominated</p>
              <h3 className="font-serif-display text-xl text-[#1C110C]">Vendor packages & responsibility</h3>
            </div>
            <p className="text-[11px] text-[#7A6B5F]">{external.length} packages</p>
          </div>
          <ExternalSupplyTable items={external} canEdit={canEdit} slug={slug} onChanged={reload} onTrack={(it) => setTrackPkg(it)} />
        </section>
      )}

      {/* Visibility hint banner (admin only) */}
      {canEdit && (
        <div className="bg-[#FAF5EC] border border-[#D4A963]/40 p-3 flex items-start gap-2 text-[11px] text-[#3E2A22]">
          <Eye size={14} className="text-[#A6822F] mt-0.5 shrink-0" />
          <div>
            <span className="text-[#1C110C] font-medium">Client visibility:</span>
            {SHARE_TOGGLE_FIELDS.filter((f) => visibility[f.k]).length === 0
              ? " no financial section is shared with clients right now."
              : (
                <>
                  {" "}sharing —{" "}
                  {SHARE_TOGGLE_FIELDS.filter((f) => visibility[f.k]).map((f) => f.l.replace(/^Share /, "").replace(/ with client$/i, "")).join(", ") || ""}.
                </>
              )}
            {" "}<button type="button" onClick={() => setShowSettings(true)} className="text-[#6E1F2C] underline">Adjust</button>
          </div>
        </div>
      )}

      {showUpload && (
        <UploadWizard slug={slug} onClose={() => setShowUpload(false)} onImported={reload} />
      )}
      {trackPkg && (
        <VendorPackageModal
          slug={slug}
          packageId={trackPkg.id}
          canEdit={canEdit}
          onClose={() => setTrackPkg(null)}
          onChanged={reload}
        />
      )}
      {showSettings && (
        <SettingsModal
          slug={slug}
          summary={summary}
          visibility={visibility}
          onClose={() => setShowSettings(false)}
          onSaved={reload}
        />
      )}
    </div>
  );
}
